/**
 * The link that lets a person into the cockpit to set a password (PLAN-MULTI-TENANT.md § 10.2, lot L6f; audit of
 * L6h + L6f, A1): an invitation (team-invite) or a password recovery (admin-update-user, `send_reset_email`).
 *
 * The functions build it on the cockpit itself (`supabase/functions/_shared/authLinks.ts`):
 * `/reset-password?token_hash=…&type=invite|recovery`. The page turns it into a session with `verifyOtp`, which works in
 * the PKCE flow the cockpit's client uses — Supabase Auth's own links return in the implicit flow, which that client
 * refuses. Pure: reading the address only, the verification is `useAuthLinkSession`'s.
 */

export type AuthLinkType = 'invite' | 'recovery';

export interface AuthLink {
  readonly tokenHash: string;
  readonly type: AuthLinkType;
}

const TYPES: readonly AuthLinkType[] = ['invite', 'recovery'];
// Supabase Auth's hashed tokens are hexadecimal; anything else is not one of our links.
const TOKEN_HASH = /^[0-9a-f]{20,128}$/i;

/** The link carried by a query string (`location.search`), or null when there is none, or a malformed one. */
export function readAuthLink(search: string): AuthLink | null {
  const params = new URLSearchParams(search);
  const tokenHash = params.get('token_hash') ?? '';
  const type = params.get('type') ?? '';
  if (!TOKEN_HASH.test(tokenHash)) return null;
  const known = TYPES.find((candidate) => candidate === type);
  return known ? { tokenHash, type: known } : null;
}
